import Session from 'svg-text-to-path/entries/browser';
import OpenTypeJsFont from 'svg-text-to-path/renderer/OpenTypeJsFont';
import GoogleProvider from 'svg-text-to-path/providers/google/GoogleProvider';
import { GOOGLE_FONTS_API_KEY } from '../ClientAccount/Signature.constants';

export async function renderSvgText(svgTextToRender: string) {
  if (!svgTextToRender) {
    return svgTextToRender;
  }
  Session.defaultRenderer = OpenTypeJsFont;
  Session.defaultProviders = [GoogleProvider];
  const session = new Session(svgTextToRender, {
    googleApiKey: GOOGLE_FONTS_API_KEY,
  });
  await session.replaceAll();
  const out = session.getSvgString();
  // console.log(`rendered svg: `, out);
  return out;
}

export async function signatureTextToSvgString(
  signatureText?: string,
): Promise<string | undefined> {
  if (!signatureText) return;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

  // Create the text element
  const textElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'text',
  );

  textElement.setAttribute('x', '0');
  textElement.setAttribute('y', '20');
  textElement.setAttribute('font-size', '38');
  textElement.setAttribute('font-family', 'Shadows Into Light, cursive');
  textElement.setAttribute('fill', '#000000');
  textElement.textContent = signatureText;

  // Append the text element to the SVG
  svg.appendChild(textElement);

  appendViewBoxMetadataToElement(svg, true);

  // Render the SVG text to paths
  return renderSvgText(
    new XMLSerializer().serializeToString(svg), // Serialize the SVG to XML
  );
}

export function signatureSvgElementToSvgString(
  signatureElement?: SVGSVGElement | null,
): string | undefined {
  if (!signatureElement) return;
  const serializer = new XMLSerializer();
  return serializer.serializeToString(signatureElement);
}

export function removeClassesFromSVG(svgElement: SVGSVGElement) {
  if (svgElement && svgElement.hasAttribute('class')) {
    svgElement.removeAttribute('class');
  }
  return svgElement;
}

export function addSVGElementToDom(svgElement: SVGSVGElement) {
  // we have to add the child to an off-screen container so that the next method call (getBBox) works
  svgElement.style.position = 'absolute';
  svgElement.style.visibility = 'hidden';
  svgElement.style.width = 'auto';
  svgElement.style.height = 'auto';
  document.body.appendChild(svgElement);
}

export function removeSvgElementFromDom(svgElement: SVGSVGElement) {
  document.body.removeChild(svgElement); //cleaning up
  svgElement.removeAttribute('style');
}

/**
 *
 * @param svgElement
 * @param temporarilyAddToDom if enabled, we temporarily add the svgelemenet on the screen (hidden) to run calculations
 * @returns
 */
export function appendViewBoxMetadataToElement(
  svgElement: SVGSVGElement,
  temporarilyAddToDom: boolean = false,
  extraMetadata: object = {},
): SVGSVGElement {
  if (svgElement) {
    if (temporarilyAddToDom) {
      addSVGElementToDom(svgElement);
    }

    const boundingBox = svgElement.getBBox();

    if (temporarilyAddToDom) {
      removeSvgElementFromDom(svgElement);
    }

    const metadataObj = {
      x: boundingBox.x,
      y: boundingBox.y,
      width: boundingBox.width + (temporarilyAddToDom ? 40 : 0), // for some reason the text element width calculations are not very accurate, so we're adding a few extra pixels to the width
      height: boundingBox.height,
      ...extraMetadata,
    };

    const metadataContent = JSON.stringify(metadataObj);

    const metadataTag = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'metadata',
    );
    metadataTag.textContent = metadataContent;
    svgElement.appendChild(metadataTag);
  }
  return svgElement;
}

function svgStringToSVGElement(svgString: string): SVGSVGElement {
  const parser = new DOMParser();

  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

  const svgElement = svgDoc.documentElement;

  return svgElement as unknown as SVGSVGElement;
}

function getMetadataFromSVG(
  svgElement: SVGSVGElement,
): { x: number; y: number; width: number; height: number } | null {
  // Select the <metadata> tag within the SVG
  const metadataTag = svgElement.querySelector('metadata');

  if (metadataTag?.textContent) {
    // Parse the metadata content as JSON if it was stored as JSON string
    try {
      const metadata = JSON.parse(metadataTag.textContent);
      return metadata;
    } catch (e) {
      console.error('Error parsing svg metadata as JSON:', e);
    }
  }
  return null;
}

export function svgStringToSVGSVGElement(
  svgString: string,
  consumeViewBoxMetadata: boolean = true,
  removeClasses: boolean = true,
): SVGSVGElement | null {
  if (svgString) {
    const svgElement = svgStringToSVGElement(svgString);

    if (consumeViewBoxMetadata) {
      const boundingBox = getMetadataFromSVG(svgElement);
      if (boundingBox && boundingBox.width > 0 && boundingBox.height > 0) {
        // Set the viewBox to the bounding box dimensions
        svgElement.setAttribute(
          'viewBox',
          `${boundingBox.x} ${boundingBox.y} ${boundingBox.width} ${boundingBox.height}`,
        );

        // Optionally, set width and height to match bounding box dimensions
        if (boundingBox.width) {
          svgElement.setAttribute('width', `${boundingBox.width}`);
        }

        if (boundingBox.height) {
          svgElement.setAttribute('height', `${boundingBox.height}`);
        }
      }
    }

    if (removeClasses) {
      removeClassesFromSVG(svgElement);
    }

    return svgElement;
  }
  return null;
}

export function generateUniqueFileName(
  extension: string = 'svg',
  stringToPrepend: string = 'file',
) {
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 1e6); // generates a random number up to 999999
  return `${stringToPrepend}_${timestamp}_${randomNum}.${extension}`;
}

export function svgElementToBase64(svgElement: SVGSVGElement) {
  // Serialize the SVG element to a string
  const svgString = new XMLSerializer().serializeToString(svgElement);

  // Encode the SVG string to Base64
  const base64Svg = `data:image/svg+xml;base64,${btoa(svgString)}`;

  return base64Svg;
}
